import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { ArrowLeft, Mail } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { policies } from "../lib/policies";
import { waLink, SUPPORT_EMAIL, WHATSAPP_DISPLAY } from "../lib/mock";

const renderBlock = (block, i) => {
  if (block.type === "p") {
    return (
      <p key={i} className="text-base leading-[1.75] text-lokam-ink-soft mt-4 max-w-3xl">
        {block.text}
      </p>
    );
  }
  if (block.type === "h2") {
    return (
      <h2
        key={i}
        className="font-display font-semibold text-2xl md:text-3xl text-lokam-forest mt-12 tracking-tight"
      >
        {block.text}
      </h2>
    );
  }
  if (block.type === "h3") {
    return (
      <h3
        key={i}
        className="font-display font-semibold text-xl text-lokam-ink mt-8 tracking-tight"
      >
        {block.text}
      </h3>
    );
  }
  if (block.type === "h4") {
    return (
      <h4
        key={i}
        className="font-display font-semibold text-base uppercase tracking-[0.12em] text-lokam-tangerine mt-6"
      >
        {block.text}
      </h4>
    );
  }
  if (block.type === "ul" || block.type === "ol") {
    const Tag = block.type === "ul" ? "ul" : "ol";
    return (
      <Tag
        key={i}
        className={`mt-4 max-w-3xl ${
          block.type === "ul" ? "list-disc" : "list-decimal"
        } pl-6 space-y-2.5 text-base leading-[1.7] text-lokam-ink-soft marker:text-lokam-tangerine`}
      >
        {block.items.map((it, j) =>
          typeof it === "string" ? (
            <li key={j}>{it}</li>
          ) : (
            <li key={j}>
              <span className="font-semibold text-lokam-ink">{it.strong}</span>{" "}
              {it.body}
            </li>
          ),
        )}
      </Tag>
    );
  }
  return null;
};

export const PolicyPage = ({ policyKey }) => {
  const policy = policies[policyKey];

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${policy.title} — Lokam Health`;
  }, [policy.title]);

  return (
    <div className="bg-lokam-cream min-h-screen">
      <Header />
      <main>
        {/* Hero strip */}
        <section className="relative bg-lokam-forest text-lokam-cream overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #FFFDF9 1px, transparent 0)",
              backgroundSize: "30px 30px",
            }}
          />
          <div className="relative px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto pt-14 pb-14 md:pt-20 md:pb-20">
            <Link
              to="/"
              data-testid={`policy-back-${policy.slug}`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-lokam-cream/80 hover:text-lokam-tangerine transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to home
            </Link>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-lokam-tangerine mt-8">
              {policy.intro}
            </div>
            <h1 className="mt-3 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {policy.title}
            </h1>
            <p className="mt-5 text-sm text-lokam-cream/70">
              Last updated:{" "}
              <span className="text-lokam-cream font-medium">
                {policy.lastUpdated}
              </span>
            </p>
          </div>
        </section>

        {/* Body */}
        <section className="px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto py-14 md:py-20">
          <article
            data-testid={`policy-body-${policy.slug}`}
            className="max-w-3xl"
          >
            {policy.body.map(renderBlock)}

            {/* Contact us inline card */}
            <div className="mt-14 rounded-2xl bg-lokam-sand border border-lokam-border p-6 md:p-8">
              <div className="font-display font-semibold text-xl text-lokam-forest">
                Questions about this {policy.title.toLowerCase()}?
              </div>
              <p className="mt-2 text-sm text-lokam-ink-soft">
                Reach us whichever way feels easiest.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`policy-wa-${policy.slug}`}
                  className="inline-flex items-center gap-2 rounded-full bg-lokam-whatsapp hover:bg-lokam-whatsapp-dark text-white px-4 py-2.5 text-sm font-semibold transition-colors"
                >
                  <FaWhatsapp className="h-4 w-4" />
                  WhatsApp · {WHATSAPP_DISPLAY}
                </a>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  data-testid={`policy-mail-${policy.slug}`}
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-lokam-border hover:border-lokam-forest text-lokam-forest px-4 py-2.5 text-sm font-semibold transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </article>
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export default PolicyPage;
